import { IntegrationType, MondayIntegrationType } from "shared/integrations/integration";
import { useBoardIntegrations, useBoardTasks } from "frontend/hooks/use-board-integrations";
import { useAtomValue } from "jotai";
import { useCallback, useContext } from "react";
import { documentIdAtom } from "state-atoms/stage-atoms";
import { FooterContext } from "frontend/context/footer-context";
import type { PointInViewport } from "frontend/utils/math-utils";

export const useMondayImportIntegration = () => {
  const { documentId } = useAtomValue(documentIdAtom);
  const { boardIntegrations } = useBoardIntegrations(documentId);
  const { mondayIntegration, addMondayIntegration, tasksIntegrations } = useBoardTasks(documentId);
  const { onChooseIntegrationItems, didDropMondayElement } = useContext(FooterContext);

  const addIntegration = useCallback(
    async ({
      boardId,
      isSubitemsBoard,
      parentBoardId,
    }: {
      boardId: string;
      isSubitemsBoard?: boolean;
      parentBoardId?: string;
    }) => {
      let existingIntegration = boardIntegrations?.find((integration) => integration.configuration.boardId === boardId);
      if (!existingIntegration) {
        const taskIntegration = tasksIntegrations?.find((integration) => integration.configuration.boardId === boardId);
        existingIntegration = taskIntegration;
      }
      if (existingIntegration) {
        return existingIntegration.id;
      }

      if (!mondayIntegration) {
        return;
      }

      const integrationId = await addMondayIntegration(
        boardId,
        mondayIntegration.id,
        [],
        MondayIntegrationType.tasks,
        [],
        parentBoardId,
        isSubitemsBoard
      );
      return integrationId;
    },
    [addMondayIntegration, boardIntegrations, mondayIntegration, tasksIntegrations]
  );

  const addItems = useCallback(
    async ({ integrationId, items, pos }: { integrationId: string; items: string[]; pos?: PointInViewport }) => {
      if (pos && items.length === 1) {
        didDropMondayElement?.(
          {
            integrationId,
            integrationType: IntegrationType.monday,
            item: {
              id: items[0],
              type: "integrationItem",
            },
          },
          pos
        );
      } else {
        onChooseIntegrationItems(
          items.map((itemId) => ({
            integrationId,
            integrationType: IntegrationType.monday,
            configuration: { itemId },
          })),
          pos
        );
      }
    },
    [didDropMondayElement, onChooseIntegrationItems]
  );

  return { addIntegration, addItems };
};
